/**
 * Copyright (c) 2019-2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __awaiter, __generator } from "tslib";
import { Column, Table } from '../../mol-data/db';
import { Task } from '../../mol-task';
import { createModels } from './basic/parser';
import { BasicSchema, createBasic } from './basic/schema';
import { ComponentBuilder } from './common/component';
import { EntityBuilder } from './common/entity';
import { IndexPairBonds } from './property/bonds/index-pair';
export function getMolModels(mol, format, ctx) {
  return __awaiter(this, void 0, void 0, function () {
    var atoms, bonds, MOL, A, type_symbol, seq_id, atom_site, entityBuilder, componentBuilder, basics, models, indexA, indexB, order, pairBonds;
    return __generator(this, function (_a) {
      switch (_a.label) {
        case 0:
          atoms = mol.atoms, bonds = mol.bonds;
          MOL = Column.ofConst('MOL', mol.atoms.count, Column.Schema.str);
          A = Column.ofConst('A', mol.atoms.count, Column.Schema.str);
          type_symbol = Column.asArrayColumn(atoms.type_symbol);
          seq_id = Column.ofConst(1, atoms.count, Column.Schema.int);
          atom_site = Table.ofPartialColumns(BasicSchema.atom_site, {
            auth_asym_id: A,
            auth_atom_id: type_symbol,
            auth_comp_id: MOL,
            auth_seq_id: seq_id,
            Cartn_x: Column.asArrayColumn(atoms.x, Float32Array),
            Cartn_y: Column.asArrayColumn(atoms.y, Float32Array),
            Cartn_z: Column.asArrayColumn(atoms.z, Float32Array),
            id: Column.range(0, atoms.count - 1),
            label_asym_id: A,
            label_atom_id: type_symbol,
            label_comp_id: MOL,
            label_seq_id: seq_id,
            label_entity_id: Column.ofConst('1', atoms.count, Column.Schema.str),
            occupancy: Column.ofConst(1, atoms.count, Column.Schema.float),
            type_symbol: type_symbol,
            pdbx_PDB_model_num: Column.ofConst(1, atoms.count, Column.Schema.int)
          }, atoms.count);
          entityBuilder = new EntityBuilder();
          entityBuilder.setNames([['MOL', 'Unknown Entity']]);
          entityBuilder.getEntityId('MOL', 0
          /* Unknown */
          , 'A');
          componentBuilder = new ComponentBuilder(seq_id, type_symbol);
          componentBuilder.setNames([['MOL', 'Unknown Molecule']]);
          componentBuilder.add('MOL', 0);
          basics = createBasic({
            entity: entityBuilder.getEntityTable(),
            chem_comp: componentBuilder.getChemCompTable(),
            atom_site: atom_site
          });
          return [4
          /*yield*/
          , createModels(basics, format !== null && format !== void 0 ? format : MolFormat.create(mol), ctx)];

        case 1:
          models = _a.sent();

          if (models.frameCount > 0) {
            indexA = Column.ofIntArray(Column.mapToArray(bonds.atomIdxA, function (x) {
              return x - 1;
            }, Int32Array));
            indexB = Column.ofIntArray(Column.mapToArray(bonds.atomIdxB, function (x) {
              return x - 1;
            }, Int32Array));
            order = Column.asArrayColumn(bonds.order, Int32Array);
            pairBonds = IndexPairBonds.fromData({
              pairs: {
                indexA: indexA,
                indexB: indexB,
                order: order
              },
              count: atoms.count
            });
            IndexPairBonds.Provider.set(models.representative, pairBonds);
          }

          return [2
          /*return*/
          , models];
      }
    });
  });
} //

export { MolFormat };
var MolFormat;

(function (MolFormat) {
  function is(x) {
    return (x === null || x === void 0 ? void 0 : x.kind) === 'mol';
  }

  MolFormat.is = is;

  function create(mol) {
    return {
      kind: 'mol',
      name: mol.title,
      data: mol
    };
  }

  MolFormat.create = create;
})(MolFormat || (MolFormat = {}));

export function trajectoryFromMol(mol) {
  return Task.create('Parse MOL', function (ctx) {
    return getMolModels(mol, void 0, ctx);
  });
}